import { useState, useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { Camera, ArrowRight, Check, Upload, X, Search, Mail, AlertTriangle, Play } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { SUCCESS, ON_SUCCESS } from '../constants/colors';
import { LegalModal } from './LegalModal';
import { TERMS_VERSION, type LegalDocumentType } from '../constants/legalDocuments';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { ParallaxBackground } from './utils/parallax';
import { toast } from 'sonner';
import { AuthService, uploadProfilePhoto, UserService } from '../services/api';
import { UsernameService } from '../services/usernameService';
import { useUserData } from './UserDataContext';
import { SafeStorage } from '../utils/safeStorage';
import { auth, db } from '../services/firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { allCountries, FlagImage } from './utils/countryFlags';
import { IdentityExpansionModal } from './IdentityExpansionModal';
import { identityData, categoryStyles } from '../constants/identities';
import { MediaTrimmerModal } from './MediaTrimmerModal';
import { useTheme } from './ThemeProvider';
import { calculateAge } from './utils/timeFormat';
import { IdentityTag } from './IdentityTag';
import { MetaService } from '../services/MetaService';

interface ProfileSetupScreenProps {
  initialName: string;
  onComplete: () => void;
}

const REQUIRED_PHOTOS = 1;


// Helper: Validate username format
const isValidUsernameFormat = (username: string) => {
  // Only allow letters, numbers, underscore, and period
  return /^[a-zA-Z0-9_.]+$/.test(username);
};

/**
 * The four portfolio slots to save: this screen's value where it has one,
 * otherwise the saved profile's.
 *
 * Setup writes the whole portfolio array on every upload and removal. Built
 * from this screen's state alone, which starts empty, the first upload on an
 * account that already had photos replaced all of them with one. That is what
 * happened to onboarded users sent here by the profile race in
 * UserDataContext; nothing should send them here now, but if anything ever
 * does, their photos survive it.
 */
function keepSavedSlots(local: string[], saved?: string[] | null): string[] {
  return Array.from({ length: 4 }, (_, i) => local[i] || saved?.[i] || '');
}

export function ProfileSetupScreen({ initialName = '', onComplete }: ProfileSetupScreenProps) {
  const { userProfile, updateProfile, canChangeUsername } = useUserData();
  const { theme } = useTheme();

  // Load saved progress from SafeStorage (localStorage) for cross-session persistence
  const savedProgress = SafeStorage.getItem('profileSetupProgress');
  const initialProgress = savedProgress ? JSON.parse(savedProgress) : {};

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [currentStep, setCurrentStep] = useState(initialProgress.currentStep || 1);
  const [photos, setPhotos] = useState<string[]>(initialProgress.photos || Array(4).fill(''));
  const [portfolioPreviews, setPortfolioPreviews] = useState<string[]>(initialProgress.portfolioPreviews || Array(4).fill(''));
  const [portfolioVideos, setPortfolioVideos] = useState<string[]>(initialProgress.portfolioVideos || Array(4).fill(''));
  const [profilePictureIndex, setProfilePictureIndex] = useState<number>(initialProgress.profilePictureIndex || 0);
  const [uploading, setUploading] = useState<boolean[]>(Array(4).fill(false));
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);

  // Placeholder Account State
  const [isPlaceholder, setIsPlaceholder] = useState(false);
  // "Back to Login" asks once before signing out (see the failsafe below).
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  // Media State
  const [mediaModalOpen, setMediaModalOpen] = useState(false);
  const [mediaUrl, setMediaUrl] = useState<string | null>(null);
  const [mediaType, setMediaType] = useState<'image' | 'video'>('image');
  const [mediaStep, setMediaStep] = useState<'match' | 'square'>('match');
  const [tempMatchPhoto, setTempMatchPhoto] = useState<string | null>(null);
  const [tempVideoBlob, setTempVideoBlob] = useState<Blob | null>(null);
  const [tempTrimStart, setTempTrimStart] = useState<number | undefined>(undefined);
  const [tempTrimEnd, setTempTrimEnd] = useState<number | undefined>(undefined);
  const [originalMediaBlob, setOriginalMediaBlob] = useState<Blob | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeSlot, setActiveSlot] = useState<number | null>(null);
  const [photoMetadata, setPhotoMetadata] = useState<{ slot: number; signature: string; timestamp: number }[]>(initialProgress.photoMetadata || []);
  const [tempFileSignature, setTempFileSignature] = useState<string>('');
  const [name, setName] = useState(initialProgress.name || initialName || '');
  const [dateOfBirth, setDateOfBirth] = useState(initialProgress.dateOfBirth || '');
  const [username, setUsername] = useState(initialProgress.username || '');
  const [bio, setBio] = useState(initialProgress.bio || '');
  const [selectedIdentities, setSelectedIdentities] = useState<string[]>(initialProgress.selectedIdentities || []);
  const [country, setCountry] = useState(initialProgress.country || '');
  const [heritageFlag, setHeritageFlag] = useState(initialProgress.heritageFlag || '');
  const [countrySearch, setCountrySearch] = useState('');
  const [occupation, setOccupation] = useState(initialProgress.occupation || '');
  const [education, setEducation] = useState(initialProgress.education || '');
  const [showInDiscover, setShowInDiscover] = useState(initialProgress.showInDiscover !== undefined ? initialProgress.showInDiscover : true);
  const [isPrivate, setIsPrivate] = useState(initialProgress.isPrivate !== undefined ? initialProgress.isPrivate : false);
  const [showIdentityModal, setShowIdentityModal] = useState(false);
  const [activeCategory, setActiveCategory] = useState('');
  // 'error' is distinct from 'taken' on purpose: both block progression, but
  // only one of them is worth retrying, and showing the same ✗ for a lookup that
  // simply failed left no way to tell the difference.
  const [usernameStatus, setUsernameStatus] = useState<
    'idle' | 'checking' | 'available' | 'taken' | 'invalid' | 'error'
  >('idle');
  // Bumped to force the debounced check to run again on a retry tap.
  const [usernameCheckNonce, setUsernameCheckNonce] = useState(0);
  // Shown OVER setup rather than navigated to. Leaving /setup would discard the
  // half-filled form, and the route was unreachable from here anyway:
  // ProtectedRoute bounced an incomplete profile straight back, which is why
  // these links appeared to do nothing.
  const [legalDoc, setLegalDoc] = useState<LegalDocumentType | null>(null);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState('');
  const [legalAgreed, setLegalAgreed] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [betaAgreed, setBetaAgreed] = useState(false);
  // Step 4 is the terms/rules acceptance on its own screen. It used to sit at the
  // bottom of the identity step, where it competed with the flag grid and tag
  // pickers for attention — a consent decision shouldn't be something you scroll
  // past on the way to the Finish button.
  const totalSteps = 4;

  // Save progress to SafeStorage whenever relevant state changes
  useEffect(() => {
    const progress = {
      currentStep,
      photos,
      portfolioPreviews,
      profilePictureIndex,
      name,
      dateOfBirth,
      username,
      bio,
      selectedIdentities,
      country,
      heritageFlag,
      occupation,
      education,
      showInDiscover,
      isPrivate,
      photoMetadata,
    };
    SafeStorage.setItem('profileSetupProgress', JSON.stringify(progress));
  }, [currentStep, photos, portfolioPreviews, profilePictureIndex, name, dateOfBirth, username, bio, selectedIdentities, country, heritageFlag, occupation, education, showInDiscover, isPrivate, photoMetadata]);

  // Start each step at the top. Without this, advancing from the long identity
  // step leaves the next screen scrolled down to wherever the reader was.
  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0 });
  }, [currentStep]);

  // Warn user before closing tab/browser (page refresh/close protection)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = 'Are you sure you want to leave? Your progress will be lost.';
      return e.returnValue;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Hydrate photographs from userProfile (Firestore) if they exist
  useEffect(() => {
    if (userProfile?.portfolio && userProfile.portfolio.length > 0) {
      // Only hydrate if the photos state is mostly empty (initial load)
      const isPhotosEmpty = photos.every(p => !p);
      if (isPhotosEmpty) {
        // Pad portfolio to 4 slots always to prevent undefined gaps
        const hydratedPhotos = [...(userProfile.portfolio || [])];
        while (hydratedPhotos.length < 4) hydratedPhotos.push('');
        setPhotos(hydratedPhotos);

        if (userProfile.portfolioPreviews) {
          const hydratedPreviews = [...userProfile.portfolioPreviews];
          while (hydratedPreviews.length < 4) hydratedPreviews.push('');
          setPortfolioPreviews(hydratedPreviews);
        }

        if (userProfile.profilePictureIndex !== undefined) setProfilePictureIndex(userProfile.profilePictureIndex);
        if (userProfile.photoMetadata) setPhotoMetadata(userProfile.photoMetadata);
        if (userProfile.portfolioVideos) {
          const hydratedVideos = [...userProfile.portfolioVideos];
          while (hydratedVideos.length < 4) hydratedVideos.push('');
          setPortfolioVideos(hydratedVideos);
        }
      }
    }
  }, [userProfile]);

  // Google accounts claim their username in the modal immediately after
  // connecting, before this screen renders, so it is already theirs and already
  // in the registry. The field is therefore not shown at all here — asking again
  // implies the first answer did not take, and an edit would collide with the
  // registry entry they already hold. The value is still mirrored into local
  // state so everything downstream (validation, the save) sees it.
  const usernamePreClaimed = !!(userProfile as any)?.username
    && (userProfile as any)?.authProvider === 'google';

  useEffect(() => {
    const claimed = (userProfile as any)?.username;
    if (usernamePreClaimed && claimed) {
      const clean = String(claimed).replace(/^@/, '').toLowerCase();
      if (username !== clean) setUsername(clean);
    }
  }, [usernamePreClaimed, userProfile, username]);

  // Check for placeholder account on mount
  useEffect(() => {
    const checkPlaceholder = async () => {
      const authInstance = await import('../services/firebaseConfig').then(m => m.auth);
      if (authInstance.currentUser) {
        const isTemp = authInstance.currentUser.email?.endsWith('@temp.afroyap.com') || false;
        setIsPlaceholder(isTemp);
        if (isTemp) {
          const derivedUsername = authInstance.currentUser.email?.split('@')[0] || '';
          if (derivedUsername && !username) {
            setUsername(derivedUsername);
          }
        }
      }
    };
    checkPlaceholder();
  }, [username]);

  // Debounce username check with format validation
  useEffect(() => {
    // Skipped for a pre-claimed username: it is registered to this account
    // already, so the check would report "taken" against its own owner.
    if (usernamePreClaimed) return;

    const checkUsername = async () => {
      if (username.length < 4) {
        setUsernameStatus('invalid');
        return;
      }

      // Check format first
      if (!isValidUsernameFormat(username)) {
        setUsernameStatus('invalid');
        return;
      }

      setUsernameStatus('checking');
      try {
        setUsernameStatus(await UsernameService.checkUsernameStatus(username));
      } catch (error) {
        // Should not happen — checkUsernameStatus resolves 'error' rather than
        // throwing — but a reachable state is better than a stuck spinner.
        console.error('Error checking username:', error);
        setUsernameStatus('error');
      }
    };

    const timeoutId = setTimeout(() => {
      if (username) {
        checkUsername();
      } else {
        setUsernameStatus('idle');
      }
    }, 300);

    return () => clearTimeout(timeoutId);
    // usernameCheckNonce: the retry button bumps it so a failed lookup can be
    // re-run without the user having to edit the name to re-trigger the effect.
  }, [username, usernameCheckNonce]);

  const handleSlotClick = (index: number) => {
    setActiveSlot(index);
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || activeSlot === null) return;

    // Validate file type and size (max 5MB)
    if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
      toast.error('Please upload an image or video file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size should be less than 10MB');
      return;
    }

    // Check for duplicate photo (using signature)
    const signature = `${file.name}-${file.size}-${file.lastModified}`;
    const isDuplicate = photoMetadata.some(m => m.signature === signature && m.slot !== activeSlot);

    if (isDuplicate) {
      toast.error('You cannot upload the same file twice.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setTempFileSignature(signature);
    setMediaType(file.type.startsWith('video/') ? 'video' : 'image');
    
    // Using URL.createObjectURL for better performance with large video/image files
    const objectUrl = URL.createObjectURL(file);
    setOriginalMediaBlob(file);
    setMediaUrl(objectUrl);
    setMediaStep('match'); // Start with Match Pane crop
    setMediaModalOpen(true);

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const dataURLToBlob = (dataURL: string) => {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  const handleMediaSave = async (data: { photoUrl: string; videoBlob?: Blob; pickedStillUrl?: string; trimStart?: number; trimEnd?: number }) => {
    const { photoUrl, videoBlob, pickedStillUrl, trimStart, trimEnd } = data;
    if (activeSlot === null || !auth.currentUser) return;

    if (mediaStep === 'match') {
      // Save the match pane crop and proceed to square crop
      setTempMatchPhoto(photoUrl);
      if (videoBlob) setTempVideoBlob(videoBlob);
      if (trimStart !== undefined) setTempTrimStart(trimStart);
      if (trimEnd !== undefined) setTempTrimEnd(trimEnd);
      
      // 🖼️ Ensure Step 2 (Avatar) uses the still version for easier cropping
      if (pickedStillUrl) {
        setMediaUrl(pickedStillUrl);
        setMediaType('image');
      }

      setMediaStep('square');
      toast.info('Now adjust for your profile picture (Square)');
    } else {
      // Final step: Upload both images
      setMediaModalOpen(false);

      if (!tempMatchPhoto) {
        toast.error('Match photo is missing. Please try again.');
        return;
      }

      setUploading(prev => {
        const newUploading = [...prev];
        newUploading[activeSlot] = true;
        return newUploading;
      });

      try {
        // Four slots, with any slot this screen has not filled taken from the
        // saved profile, so an upload can never blank photos already on it.
        const currentPhotos = keepSavedSlots(photos, userProfile?.portfolio);
        const currentPreviews = keepSavedSlots(portfolioPreviews, userProfile?.portfolioPreviews);
        const currentVideos = keepSavedSlots(portfolioVideos, userProfile?.portfolioVideos);

        // 1. Upload Match Pane version (Vertical)
        const matchBlob = dataURLToBlob(tempMatchPhoto);
        const matchFile = new File([matchBlob], `photo-${activeSlot}-match.jpg`, { type: 'image/jpeg' });
        const matchURL = await uploadProfilePhoto(auth.currentUser.uid, matchFile, activeSlot);

        // 2. Upload Profile Preview version (Square)
        const squareBlob = dataURLToBlob(photoUrl);
        const squareFile = new File([squareBlob], `photo-${activeSlot}-square.jpg`, { type: 'image/jpeg' });
        const previewURL = await uploadProfilePhoto(auth.currentUser.uid, squareFile, activeSlot, 'preview');

        // 3. Handle Video Upload if applicable
        let finalVideoURL = '';
        const effectiveVideoBlob = videoBlob || tempVideoBlob;
        if (mediaType === 'video' && effectiveVideoBlob) {
          // Preserve the source's real content-type/extension (don't force mp4) so
          // Storage serves a Content-Type the browser can actually decode.
          const vType = effectiveVideoBlob.type?.startsWith('video/') ? effectiveVideoBlob.type : 'video/mp4';
          const vExt = vType.includes('webm') ? 'webm' : vType.includes('quicktime') ? 'mov' : vType.includes('ogg') ? 'ogv' : 'mp4';
          const videoFile = new File([effectiveVideoBlob], `video-${activeSlot}.${vExt}`, { type: vType });
          finalVideoURL = await uploadProfilePhoto(auth.currentUser.uid, videoFile, activeSlot, 'video');
        }

        // Update photo metadata
        const updatedMeta = [...(userProfile?.photoMetadata || photoMetadata)].filter(m => Number(m.slot) !== Number(activeSlot));
        if (tempFileSignature) {
          const metaObj: any = { 
            slot: activeSlot, 
            signature: tempFileSignature, 
            timestamp: Date.now()
          };
          if (tempTrimStart !== undefined) metaObj.trimStart = tempTrimStart;
          if (tempTrimEnd !== undefined) metaObj.trimEnd = tempTrimEnd;
          updatedMeta.push(metaObj);
        }
        setPhotoMetadata(updatedMeta);

        // Prepare consistent updates
        currentPhotos[activeSlot] = matchURL;
        currentPreviews[activeSlot] = previewURL;
        currentVideos[activeSlot] = finalVideoURL || currentVideos[activeSlot];

        // Update local state for immediate UI feedback
        setPhotos(currentPhotos);
        setPortfolioPreviews(currentPreviews);
        setPortfolioVideos(currentVideos);

        // 🔒 PATCH: Immediate Save to Firestore for photo persistence
        if (auth.currentUser) {
          await updateProfile({
            portfolio: currentPhotos,
            portfolioPreviews: currentPreviews,
            portfolioVideos: currentVideos,
            photoMetadata: updatedMeta,
            profilePictureIndex: profilePictureIndex >= 0 ? profilePictureIndex : activeSlot
          });
        }

        toast.success('Media uploaded and saved!');

      } catch (error) {
        console.error('[Photo Upload] Error uploading photo:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        toast.error(`Failed to upload media: ${errorMessage}`);
      } finally {
        setUploading(prev => {
          const newUploading = [...prev];
          if (activeSlot !== null) newUploading[activeSlot] = false;
          return newUploading;
        });
        setTempMatchPhoto(null);
        setTempVideoBlob(null);
        setTempTrimStart(undefined);
        setTempTrimEnd(undefined);
        setMediaUrl(prev => {
          if (prev && prev.startsWith('blob:')) {
            URL.revokeObjectURL(prev);
          }
          return null;
        });
        setOriginalMediaBlob(null);
        setMediaStep('match'); // Reset for next photo
      }
    }
  };

  const removePhoto = (index: number) => {
    // Only the slot being removed is cleared; every other slot keeps what the
    // saved profile has (see keepSavedSlots).
    const newPhotos = keepSavedSlots(photos, userProfile?.portfolio);
    newPhotos[index] = '';
    setPhotos(newPhotos);

    const newPreviews = keepSavedSlots(portfolioPreviews, userProfile?.portfolioPreviews);
    newPreviews[index] = '';
    setPortfolioPreviews(newPreviews);

    const newVideos = keepSavedSlots(portfolioVideos, userProfile?.portfolioVideos);
    newVideos[index] = '';
    setPortfolioVideos(newVideos);

    // Remove metadata for this slot
    const newMeta = photoMetadata.filter(m => m.slot !== index);
    setPhotoMetadata(newMeta);

    // Update Firestore immediately
    updateProfile({
      portfolio: newPhotos,
      portfolioPreviews: newPreviews,
      portfolioVideos: newVideos,
      photoMetadata: newMeta
    }).catch(err => console.error('Failed to sync photo removal:', err));
  };

  const identityCategories = [
    { name: 'Ethnic Groups', samples: ['Yoruba', 'Igbo', 'Akan', 'Zulu', 'Swahili', 'Amhara'] },
    { name: 'Religions', samples: ['Christianity', 'Islam', 'Traditional African', 'Judaism', 'Rastafarianism'] },
    { name: 'Music', samples: ['Afrobeats', 'Amapiano', 'Dancehall', 'Reggae', 'Hip-Hop', 'Gospel'] },
    { name: 'Beliefs', samples: ['Pan-Africanism', 'Ubuntu', 'Social Justice', 'Afrofuturism', 'Black Excellence'] },
    { name: 'Occupations', samples: ['Student', 'Artist', 'Developer', 'Entrepreneur', 'Healthcare Worker'] },
    { name: 'Interests', samples: ['Music', 'Art', 'Fashion', 'Tech', 'Sports', 'Travel'] },
  ];

  const toggleIdentity = (identity: string) => {
    if (selectedIdentities.includes(identity)) {
      setSelectedIdentities(selectedIdentities.filter(i => i !== identity));
    } else if (selectedIdentities.length < 10) {
      setSelectedIdentities([...selectedIdentities, identity]);
    } else {
      toast.error('You can select up to 10 identities');
    }
  };

  const isAgeValid = (dob: string) => {
    if (!dob) return false;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age >= 16 && age <= 100;
  };

  const canProceedToNext = () => {
    if (currentStep === 1) {
      const uploadedCount = photos.filter(p => p).length;
      return uploadedCount >= REQUIRED_PHOTOS && profilePictureIndex >= 0 && photos[profilePictureIndex] !== '';
    }
    if (currentStep === 2) {
      const hasName = name.trim().length > 0;
      const hasValidAge = isAgeValid(dateOfBirth);
      const hasCountry = country.trim().length > 0;

      if (isPlaceholder) {
        return hasName && hasValidAge && hasCountry;
      }

      // A pre-claimed username is already registered to this account, so the
      // availability check would correctly report it as taken — by them — and
      // block the step forever. It is valid by virtue of already being theirs.
      const hasValidUsername = usernamePreClaimed || usernameStatus === 'available';
      const canProceed: boolean = hasName && hasValidAge && hasValidUsername && hasCountry;

      return canProceed;
    }
    if (currentStep === 3) {
      const hasHeritageFlag = heritageFlag.trim().length > 0;
      return selectedIdentities.length >= 1 && hasHeritageFlag;
    }
    if (currentStep === 4) {
      return betaAgreed;
    }
    return false;
  };

  const handleNext = () => {

    if (!canProceedToNext()) {
      if (currentStep === 1) {
        const uploadedCount = photos.filter(p => p).length;
        if (uploadedCount < REQUIRED_PHOTOS) {
          toast.error(`Please upload at least 1 photo to continue`);
        } else if (!photos[profilePictureIndex]) {
          toast.error('Please select a valid profile media');
        }
      }
      if (currentStep === 2) {
        if (name.trim().length === 0) toast.error('Please enter your name');
        else if (dateOfBirth.trim().length === 0) toast.error('Please enter your date of birth');
        else if (!isAgeValid(dateOfBirth)) toast.error('You must be between 16 and 100 years old');

        else if (isPlaceholder || usernamePreClaimed) {
          // No username to complain about: it was either derived from the
          // placeholder email or already claimed when Google was connected.
          if (country.trim().length === 0) toast.error('Please select a country');
        } else {
          // Validate USERNAME (standard flow)
          if (usernameStatus === 'taken') toast.error('Username is already taken');
          else if (auth.currentUser?.email && username.toLowerCase() === auth.currentUser.email.split('@')[0].toLowerCase() && !isPlaceholder) toast.error('Please choose a different username');
          else if (usernameStatus === 'checking') toast.error('Checking username availability...');
          else if (usernameStatus === 'idle') toast.error('Please enter a username');
          else if (usernameStatus === 'invalid') toast.error('Username must be at least 4 characters');
          else if (usernameStatus === 'error') toast.error("Couldn't check that username. Tap retry and try again.");
          else if (country.trim().length === 0) toast.error('Please select a country');
        }
      }
      if (currentStep === 3) {
        if (heritageFlag.trim().length === 0) toast.error('Please select your heritage flag');
        else if (selectedIdentities.length < 1) toast.error('Please select at least 1 identity');
      }
      if (currentStep === 4) {
        if (!betaAgreed) toast.error('Please accept the Terms and Community Rules to continue');
      }
      return;
    }

    // Additional age validation for Step 2 before proceeding to Step 3
    if (currentStep === 2) {
      if (dateOfBirth) {
        const age = calculateAge(dateOfBirth) || 0;
        if (age < 16) {
          toast.error('You must be at least 16 years old to use AfroYap');
          setError('You must be at least 16 years old to use AfroYap');
          return;
        }
      }
    }

    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleComplete = async () => {
    // Validate age (16+) if birthday is provided
    if (dateOfBirth) {
      const age = calculateAge(dateOfBirth) || 0;
      if (age < 16) {
        toast.error('You must be at least 16 years old to use AfroYap');
        return;
      }
    } else {
      toast.error('Please enter your date of birth');
      return;
    }

    setCompleting(true);

    try {
      const residenceCountry = country; 

      // 🏳️ ORIGIN: Heritage/origin country — determines the flag on posts
      const heritageCountry = allCountries.find(c => c.code === heritageFlag);
      const heritageCountryName = heritageCountry ? heritageCountry.name : residenceCountry;

      // TODO (Phase 2a follow-up): add a DOB confirmation dialog before this write
      // ("You entered X — you'll be Y years old. Is that correct?"). The DOB lock
      // (firestore.rules isDobLockedChange) prevents self-correction after onboarding;
      // this single write is the only non-admin path to set DOB. Raising the stakes
      // on one input warrants an explicit confirmation step.

      // Update user profile with the collected data
      const profileUpdates: any = {
        name: name?.trim().replace(/\s+/g, ' '),
        dateOfBirth: dateOfBirth,
        bio,
        portfolio: photos,
        photoMetadata,
        portfolioPreviews: portfolioPreviews,
        portfolioVideos: portfolioVideos,
        profilePictureIndex: profilePictureIndex,
        identities: selectedIdentities,
        country: heritageCountryName,   
        location: residenceCountry,  
        home_residence: residenceCountry,
        occupation,
        education,
        showInDiscover,
        isPrivate,
        countryFlag: heritageFlag, 
        image: photos[profilePictureIndex] || portfolioPreviews[profilePictureIndex] || '',
        avatar: portfolioPreviews[profilePictureIndex] || photos[profilePictureIndex] || '',
      };


      // 🔒 PATCH: Always update username to ensure it's correctly set in Firestore
      if (username) {
        profileUpdates.username = `@${username.trim().replace(/^@+/, '')}`;
      }

      // Legal compliance: log policy acceptance timestamp and version.
      // TERMS_VERSION lives with the documents, so the text and the version it
      // refers to cannot be updated independently of each other.
      profileUpdates.policyAcceptedAt = new Date().toISOString();
      profileUpdates.policyVersion = TERMS_VERSION;

      // Mark setup as complete — required for user to appear on Discover screen
      profileUpdates.setupComplete = true;

      // When they finished, and how long it took.
      //
      // setupComplete was a bare boolean, so nothing recorded WHEN onboarding
      // finished — only that it had. Both are written: the timestamp so the
      // duration can be recomputed later if the arithmetic here is ever wrong,
      // and the duration itself so reading it costs no second lookup.
      //
      // Measured from account creation, which is the only start point that
      // exists server-side. It therefore counts an abandoned-and-resumed signup
      // as one long session, so treat a very large value as "came back later"
      // rather than "spent an hour on the form" — the median is the useful
      // number here, not the mean.
      const finishedAt = new Date();
      profileUpdates.setupCompletedAt = finishedAt.toISOString();
      {
        const createdRaw: any = (userProfile as any)?.createdAt;
        const createdAt = createdRaw?.toDate ? createdRaw.toDate() : (createdRaw ? new Date(createdRaw) : null);
        if (createdAt && !Number.isNaN(createdAt.getTime())) {
          const ms = finishedAt.getTime() - createdAt.getTime();
          if (ms > 0) profileUpdates.onboardingDurationMs = ms;
        }
      }

      await updateProfile(profileUpdates);

      // Conversion signal, fired HERE rather than when the auth record was
      // created: an abandoned half-signup is not a conversion, and counting one
      // would quietly inflate every cost-per-registration figure the ad account
      // reports. This is the moment an account becomes a real user.
      //
      // Deliberately not awaited. Tracking must never delay, or fail, the thing
      // it is tracking — the service swallows its own errors as well.
      void MetaService.trackSignup({
        userId: String(userProfile?.id || ''),
        email: (userProfile as any)?.email || '',
        country: heritageCountryName || '',
      });

      // Clear saved progress — App.tsx also calls this, but belt-and-suspenders
      SafeStorage.removeItem('profileSetupProgress');

      // Flag: prompt new users to set up account recovery right after onboarding
      // This is read by App.tsx in the onComplete callback before navigating to Explore
      SafeStorage.setItem('pending_security_setup', 'true');

      // Flag: show the community guidelines once, to this new account only.
      // AppRouter shows the modal on this flag alone and clears it on accept, so
      // an existing account never sees it — it reaches the rules through the
      // Rules button in Settings instead.
      SafeStorage.setItem('pending_guidelines', 'true');

      // Flag: run the app guide once, to this new account only. Same gate as the
      // guidelines above, for the same reason — it marks "just signed up", not
      // "has never seen it". Existing accounts reach it from Settings instead.
      SafeStorage.setItem('pending_guide', 'true');

      // Show welcome notification
      toast.success('Welcome to AfroYap! Respect the rules and have fun!', {
        duration: 5000,
      });

      onComplete();
    } catch (error) {
      console.error('Error completing profile setup:', error);
      if (error instanceof Error && error.message) {
        toast.error(error.message);
      } else {
        toast.error('Failed to complete profile setup. Please try again.');
      }
      setCompleting(false);
    }
  };

  const handleOpenIdentityModal = (category: string) => {
    setActiveCategory(category);
    setShowIdentityModal(true);
  };

  const handleIdentitiesSave = (identities: string[]) => {
    setSelectedIdentities(identities);
  };

  return (
    <div className="fixed inset-0 z-50 bg-light-pattern dark:bg-[#0D0D0D] flex flex-col overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <ParallaxBackground speed={0.05} />
      </div>

      {/* Fixed Header */}
      <div className="flex-none w-full max-w-md mx-auto px-6 relative z-10 bg-[#eddeb7]/95 dark:bg-[#0D0D0D]/95 backdrop-blur-sm pt-[env(safe-area-inset-top)] pb-4 border-b border-black/5 dark:border-white/5 transition-colors">
         <div style={{ height: 'max(20px, env(safe-area-inset-top))' }} /> 
         
          {/* Progress Bar */}
          <div className="w-full h-1 bg-black/10 dark:bg-[#E5E5E5]/10 rounded-full mb-6 overflow-hidden shrink-0 mt-4">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${(currentStep / totalSteps) * 100}%` }}
              transition={{ duration: 0.3 }}
               className="h-full bg-linear-to-r from-[#4478ea] to-[#2d1ef1]"
            />
          </div>

          <div className="text-center">
             <h1 className="text-2xl text-[#4478ea] dark:text-[#E5E5E5] mb-1 font-bold">
               Complete Your Profile
             </h1>
             <p className="text-sm font-medium text-[#666666] dark:text-[#a0a0a0]">
               Step {currentStep} of {totalSteps}
             </p>
           </div>
      </div>

      {/* Scrollable Content Area */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto w-full min-h-0 relative z-10 scrollbar-hide pb-[calc(8rem+env(safe-area-inset-bottom))]">
        <motion.div
          className="w-full max-w-md mx-auto px-6 py-6"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="w-full">

            {/* Step 1: Upload Photos */}
            {currentStep === 1 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="mb-4">
                  <h2 className="text-lg text-[#4478ea] dark:text-[#E5E5E5] mb-1 font-bold">
                    Add Your Media
                  </h2>
                  <p className="text-xs text-[#666666] dark:text-[#a0a0a0] mb-3">
                    Upload at least 1 media. You can add up to 4. Tap one to set as your avatar.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    className="hidden"
                    accept="image/*,video/*"
                  />
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div key={index} className="relative aspect-square">
                      {photos[index] ? (
                        <div
                          className="absolute inset-0 rounded-2xl overflow-hidden cursor-pointer group card-shadow pointer-events-auto"
                          onClick={() => setProfilePictureIndex(index)}
                        >
                            <img
                              src={portfolioPreviews[index] || photos[index]}
                              alt={`Media ${index + 1}`}
                              className="w-full h-full object-cover object-center"
                            />
                           {/* Avatar Indicator Badge - Pill style */}
                           {profilePictureIndex === index && (
                             <div className="absolute bottom-2 right-2 bg-black/60 backdrop-blur-md py-0.5 px-2 rounded-full border border-white/20 shadow-lg flex items-center justify-center">
                               <span className="text-[6px] font-bold text-white tracking-wider uppercase">Avatar</span>
                             </div>
                           )}
                          <div className="absolute top-1 right-1 flex gap-1 opacity-100 transition-opacity">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSlotClick(index);
                              }}
                              className="w-6 h-6 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center hover:bg-black/80"
                            >
                              <Upload className="w-3 h-3 text-white" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                removePhoto(index);
                              }}
                              className="w-6 h-6 rounded-full bg-red-500/80 backdrop-blur-sm flex items-center justify-center hover:bg-red-600"
                            >
                              <X className="w-3 h-3 text-white" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleSlotClick(index)}
                          disabled={uploading[index]}
                          className="w-full h-full rounded-xl border-2 border-dashed border-[#4478ea] flex flex-col items-center justify-center gap-1 hover:bg-[#4478ea]/10 transition-colors relative"
                        >
                          {uploading[index] ? (
                            <div className="w-6 h-6 border-2 border-[#4478ea] border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <>
                              <Upload className="w-6 h-6 text-[#4478ea]" />
                              <span className="text-xs text-[#4478ea]">
                                {index === 0 ? 'Avatar Media' : `Media ${index + 1}`}
                              </span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Logout Failsafe. Two taps, with a warning between them: most
                    accounts sign in with a username and password and have no
                    recovery email, so signing out by accident can lose the
                    account for good. */}
                <div className="mt-6 pt-4 border-t border-black/5 dark:border-white/5 text-center px-2">
                  {confirmSignOut && (
                    <p style={{ fontSize: 12, lineHeight: 1.4, marginBottom: 8, color: '#E74C3C' }}>
                      You will need your username and password to sign back in. Tap again to sign out.
                    </p>
                  )}
                  <button
                    onClick={async () => {
                      if (!confirmSignOut) { setConfirmSignOut(true); return; }
                      SafeStorage.removeItem('profileSetupProgress');
                      try {
                        await auth.signOut();
                      } catch (e) {
                        console.error('Sign out error', e);
                      }
                      window.dispatchEvent(new CustomEvent('navigateToScreen', { detail: 'login' }));
                    }}
                    className="text-[11px] text-[#4478ea] dark:text-[#a0a0a0] hover:underline font-bold"
                  >
                    {confirmSignOut ? 'Sign out' : '← Back to Login'}
                  </button>
                  {confirmSignOut && (
                    <button
                      onClick={() => setConfirmSignOut(false)}
                      className="text-[11px] text-[#4478ea] dark:text-[#a0a0a0] hover:underline font-bold"
                      style={{ marginLeft: 16 }}
                    >
                      Stay
                    </button>
                  )}
                </div>
              </motion.div>
            )}

            {/* Step 2: Username & Bio */}
            {currentStep === 2 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
                className="space-y-6"
              >
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label 
                        className="block text-xs font-medium text-[#4478ea] dark:text-[#E5E5E5] mb-1"
                        style={{ WebkitTextStroke: '0px' }}
                      >
                        Display Name *
                      </label>
                      <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Name"
                        className="h-10 text-sm bg-[#f2deb3] dark:bg-[#2a2a2a] border-black/10 dark:border-white/10 text-[#4478ea] dark:text-white focus:ring-1 focus:ring-[#4478ea] dark:focus:ring-[#4478ea] focus:border-transparent"
                        maxLength={50}
                      />
                    </div>
                    <div>
                      <label 
                        className="block text-xs font-medium text-[#4478ea] dark:text-[#E5E5E5] mb-1"
                        style={{ WebkitTextStroke: '0px' }}
                      >
                        Date of Birth *
                      </label>
                      <Input
                        type="date"
                        value={dateOfBirth}
                        onChange={(e) => setDateOfBirth(e.target.value)}
                        min={(() => {
                          const date = new Date();
                          date.setFullYear(date.getFullYear() - 100);
                          return date.toISOString().split('T')[0];
                        })()}
                        max={(() => {
                          const date = new Date();
                          date.setFullYear(date.getFullYear() - 16);
                          return date.toISOString().split('T')[0];
                        })()}
                        className="h-10 text-sm bg-[#f2deb3] dark:bg-[#2a2a2a] border-black/10 dark:border-white/10 text-[#4478ea] dark:text-white focus:ring-1 focus:ring-[#4478ea] dark:focus:ring-[#4478ea] focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label 
                      className="block text-xs font-medium text-[#4478ea] dark:text-[#E5E5E5] mb-1"
                      style={{ WebkitTextStroke: '0px' }}
                    >
                      About Me
                    </label>
                    <Textarea
                      value={bio}
                      onChange={(e) => setBio(e.target.value)}
                      placeholder="What do you want other yappers to know?"
                      className="min-h-[60px] h-16 text-sm bg-[#f2deb3] dark:bg-[#2a2a2a] border-black/10 dark:border-white/10 text-[#4478ea] dark:text-white resize-none focus:ring-1 focus:ring-[#4478ea] dark:focus:ring-[#4478ea] focus:border-transparent"
                      maxLength={150}
                    />
                  </div>



                  {/* Hidden for accounts that already chose a username when they
                      connected Google — asking a second time implies the first
                      answer did not take. */}
                  {!isPlaceholder && !usernamePreClaimed && (
                  <div>
                    <label
                      className="block text-xs font-medium text-[#4478ea] dark:text-[#E5E5E5] mb-1"
                      style={{ WebkitTextStroke: '0px' }}
                    >
                      Username *
                    </label>
                    <div className="relative">
                      <Input
                        value={username}
                        onChange={(e) => setUsername(e.target.value.toLowerCase())}
                        placeholder="username"
                        className="h-10 pl-8 text-sm bg-[#f2deb3] dark:bg-[#2a2a2a] border-black/10 dark:border-white/10 text-[#4478ea] dark:text-white focus:ring-1 focus:ring-[#4478ea] dark:focus:ring-[#4478ea] focus:border-transparent"
                        maxLength={20}
                      />
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666666] dark:text-[#a0a0a0] text-sm">@</span>
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                        {usernameStatus === 'checking' && <div className="w-3.5 h-3.5 border-2 border-[#4478ea] border-t-transparent rounded-full animate-spin" />}
                        {usernameStatus === 'available' && <Check className="w-4 h-4 text-green-500" />}
                        {usernameStatus === 'taken' && <X className="w-4 h-4 text-red-500" />}
                        {usernameStatus === 'invalid' && <AlertTriangle className="w-4 h-4 text-amber-500" />}
                        {usernameStatus === 'error' && <AlertTriangle className="w-4 h-4 text-amber-500" />}
                      </div>
                    </div>
                    {usernameStatus === 'taken' && <p className="text-[10px] text-red-500 mt-1 ml-1 font-medium">This username is already taken</p>}
                    {usernameStatus === 'invalid' && <p className="text-[10px] text-amber-500 mt-1 ml-1 font-medium">Use only letters, numbers, underscores, and periods (min. 4)</p>}
                    {usernameStatus === 'available' && <p className="text-[10px] text-green-500 mt-1 ml-1 font-medium italic">Username is available!</p>}
                    {usernameStatus === 'error' && (
                      <button
                        type="button"
                        onClick={() => setUsernameCheckNonce(n => n + 1)}
                        className="text-[10px] text-amber-500 mt-1 ml-1 font-medium underline"
                      >
                        Couldn't check availability, tap to retry
                      </button>
                    )}
                  </div>
                  )}

                  <div>
                    <label 
                      className="block text-xs font-medium text-[#4478ea] dark:text-[#E5E5E5] mb-1"
                      style={{ WebkitTextStroke: '0px' }}
                    >
                      Where Do You Live? *
                    </label>
                    <Select
                      value={country}
                      onValueChange={(value: string) => {
                        setCountry(value);
                        setCountrySearch('');
                      }}
                    >
                      <SelectTrigger
                        className="h-10 text-sm bg-[#f2deb3] dark:bg-[#2a2a2a] border-black/10 dark:border-white/10 text-[#4478ea] dark:text-white"
                      >
                        <SelectValue placeholder="Select Country" />
                      </SelectTrigger>
                      <SelectContent
                        className="bg-light-pattern dark:bg-[#1a1a1a] border-black/10 dark:border-white/10 text-[#4478ea] dark:text-white max-h-[40vh]"
                        position="popper"
                        sideOffset={5}
                      >
                        <div className="p-2 border-b border-black/10 dark:border-white/10 bg-light-pattern dark:bg-[#1a1a1a] sticky top-0 z-10">
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666666] dark:text-[#999999] pointer-events-none" />
                            <input
                              type="text"
                              value={countrySearch}
                              onChange={(e) => {
                                e.stopPropagation();
                                setCountrySearch(e.target.value);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                              placeholder="Search countries..."
                              className="w-full pl-9 h-10 bg-[#f2deb3] dark:bg-[#2a2a2a] border border-black/10 dark:border-white/10 text-[#4478ea] dark:text-white rounded-lg outline-none focus:border-[#4478ea] dark:focus:border-[#4478ea] transition-colors"
                            />
                          </div>
                        </div>
                        <div className="max-h-[200px] overflow-y-auto">
                          {allCountries
                            .filter((c) =>
                              c.name.toLowerCase().includes(countrySearch.toLowerCase())
                            )
                            .map((c) => (
                              <SelectItem key={c.code} value={c.name}>
                                {c.name}
                              </SelectItem>
                            ))}
                        </div>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label 
                        className="block text-xs font-medium text-[#4478ea] dark:text-[#E5E5E5] mb-1"
                        style={{ WebkitTextStroke: '0px' }}
                      >
                        Occupation
                      </label>
                      <Input
                        value={occupation}
                        onChange={(e) => setOccupation(e.target.value)}
                        placeholder="e.g. Artist"
                        className="h-10 text-sm bg-[#f2deb3] dark:bg-[#2a2a2a] border-black/10 dark:border-white/10 text-[#4478ea] dark:text-white focus:ring-1 focus:ring-[#4478ea] dark:focus:ring-[#4478ea] focus:border-transparent"
                        maxLength={50}
                      />
                    </div>
                    <div>
                      <label 
                        className="block text-xs font-medium text-[#4478ea] dark:text-[#E5E5E5] mb-1"
                        style={{ WebkitTextStroke: '0px' }}
                      >
                        Education
                      </label>
                      <Input
                        value={education}
                        onChange={(e) => setEducation(e.target.value)}
                        placeholder="e.g. Student"
                        className="h-10 text-sm bg-[#f2deb3] dark:bg-[#2a2a2a] border-black/10 dark:border-white/10 text-[#4478ea] dark:text-white focus:ring-1 focus:ring-[#4478ea] dark:focus:ring-[#4478ea] focus:border-transparent"
                        maxLength={50}
                      />
                    </div>
                  </div>

                </div>
                
                <div className="pt-6 pb-2 text-center opacity-60">
                  <p className="text-[8px] text-[#666666] dark:text-[#a0a0a0] italic font-medium tracking-tight">
                    * means question is required to move on
                  </p>
                </div>
              </motion.div>
            )}

            {/* Step 3: Select Identities */}
            {currentStep === 3 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="mb-8 text-center">
                  <h2 className="text-sm font-bold text-[#4478ea] dark:text-[#E5E5E5] mb-2">
                    Your Identity & Roots
                  </h2>
                  <p className="text-[10px] text-[#666666] dark:text-[#a0a0a0] mb-2">
                    Select your heritage flag and at least one identity category.
                  </p>
                </div>

                {/* Heritage Flag Selection (Moved to Step 3) */}
                <div className="mb-6">
                  <label 
                    className="block text-xs font-medium text-[#4478ea] dark:text-[#E5E5E5] mb-2"
                    style={{ WebkitTextStroke: '0px' }}
                  >
                    Heritage/Origin *
                  </label>
                  <p className="text-[10px] text-[#666666] dark:text-[#a0a0a0] mb-2">
                    Select the flag that represents your heritage or origin
                  </p>
                  <div className="grid grid-cols-3 gap-2 max-h-[240px] overflow-y-auto p-2 bg-black/5 dark:bg-white/5 rounded-xl border border-black/5 dark:border-white/5">
                    {allCountries.map((flagCountry) => (
                      <button
                        key={flagCountry.code}
                        type="button"
                        onClick={() => {
                          console.log('Setting heritage flag to:', flagCountry.code);
                          setHeritageFlag(flagCountry.code);
                          toast.success(`${flagCountry.name} selected as heritage`, {
                            duration: 1500,
                            position: 'bottom-center'
                          });
                        }}
                        className={`p-2 rounded-lg border-2 transition-all flex flex-col items-center gap-1 cursor-pointer relative z-20 ${
                          heritageFlag === flagCountry.code
                            ? 'border-[#4478ea] bg-[#4478ea]/10 dark:bg-[#4478ea]/20'
                            : 'border-black/10 dark:border-white/10 bg-white dark:bg-[#1a1a1a] hover:border-[#4478ea]/50'
                        }`}
                      >
                        <div className="w-12 h-9 rounded overflow-hidden border border-black/10 dark:border-white/10 pointer-events-none">
                          <FlagImage
                            countryName={flagCountry.name}
                            size="w80"
                            className={`w-full h-full ${(flagCountry.name.toLowerCase() === 'soulaan' || flagCountry.name.toLowerCase() === 'republic of new afrika' || flagCountry.name.toLowerCase() === 'gullah geechie') ? 'object-contain' : 'object-cover'}`}
                          />
                        </div>
                        <span className="text-[9px] text-black dark:text-white text-center line-clamp-1 pointer-events-none">
                          {flagCountry.name}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  {identityCategories.map((category) => {
                    const categoryValues = identityData[category.name] || [];
                    const userSelected = selectedIdentities.filter(id => categoryValues.includes(id));
                    const otherSamples = category.samples.filter(id => !selectedIdentities.includes(id));

                    return (
                      <div key={category.name}>
                        <div className="flex items-center justify-between mb-3 px-1">
                          <h3 className="text-lg font-bold text-black dark:text-white">
                            {category.name}
                          </h3>
                          <Button
                            variant="ghost"
                            onClick={() => handleOpenIdentityModal(category.name)}
                            className="px-3 py-1 rounded-full text-white shadow-md text-[10px] font-bold border-none h-auto min-h-0"
                            style={{ 
                              backgroundImage: theme === 'dark' 
                                ? 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)' 
                                : 'linear-gradient(135deg, #F39C12 0%, #E74C3C 100%)',
                              textShadow: '0px 1px 3px rgba(0,0,0,0.8)'
                            }}
                          >
                            Show More →
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-2 justify-start">
                          {/* Selected Tags */}
                          {userSelected.map((identity) => (
                            <motion.button
                              key={identity}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => toggleIdentity(identity)}
                              className="relative"
                            >
                              <IdentityTag tag={identity} variant="profile" />
                              <div className="absolute -top-1 -right-1 bg-white rounded-full p-0.5 shadow-sm border border-black/5">
                                <Check className="w-2.5 h-2.5 text-blue-600" />
                              </div>
                            </motion.button>
                          ))}
                          {/* Unselected Samples */}
                          {otherSamples.map((identity) => (
                            <motion.button
                              key={identity}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => toggleIdentity(identity)}
                            >
                              <IdentityTag tag={identity} variant="standard" />
                            </motion.button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <p className="text-sm text-[#666666] dark:text-[#a0a0a0] text-center mt-8 mb-4">
                  {selectedIdentities.length} of 10 identities selected
                </p>

                {/* Show me in Discover Toggle (Moved to Step 3) */}
                <div className="mb-6 p-3 rounded-xl bg-linear-to-r from-[#4478ea]/10 to-[#2d1ef1]/10 border border-[#4478ea]/30 flex items-center justify-between">
                  <div>
                    <p 
                      className="text-sm font-bold text-[#4478ea] dark:text-[#E5E5E5] mb-1"
                      style={{ WebkitTextStroke: '0px' }}
                    >
                      Show in Discover
                    </p>
                    <p className="text-xs text-[#666] dark:text-[#aaa] max-w-[280px]">
                      Let other yappers all over the world swipe to add you on the DIscover Page based on your interests and profile
                    </p>
                  </div>
                  <Switch
                    checked={showInDiscover}
                    onCheckedChange={setShowInDiscover}
                    className="ml-3"
                  />
                </div>

              </motion.div>
            )}

            {/* Step 4: Terms & Community Rules — deliberately alone on the page */}
            {currentStep === 4 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="mb-8 text-center">
                  <h2 className="text-sm font-bold text-[#4478ea] dark:text-[#E5E5E5] mb-2">
                    Before You Join
                  </h2>
                  <p className="text-[10px] text-[#666666] dark:text-[#a0a0a0] mb-2">
                    One last thing — please read and accept our terms and community rules.
                  </p>
                </div>

                <div
                  className={`mb-6 p-5 rounded-xl border flex flex-col items-center text-center cursor-pointer transition-colors ${
                    betaAgreed
                      ? 'border-green-400 dark:border-green-500 bg-green-50 dark:bg-green-900/20'
                      : 'border-[#4478ea]/30 bg-[#4478ea]/10'
                  }`}
                  onClick={() => setBetaAgreed(!betaAgreed)}
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 transition-colors ${
                    betaAgreed
                      ? 'bg-green-500'
                      : 'bg-linear-to-br from-[#4478ea] to-[#2d1ef1]'
                  }`}>
                    {betaAgreed ? (
                      <Check className="w-6 h-6 text-white" />
                    ) : (
                      <AlertTriangle className="w-6 h-6 text-white" />
                    )}
                  </div>
                  <h3 className={`text-base font-bold mb-2 ${
                    betaAgreed ? 'text-green-700 dark:text-green-400' : 'text-[#4478ea] dark:text-white'
                  }`}>
                    {betaAgreed ? 'Ready to Continue!' : 'Welcome to AfroYap'}
                  </h3>
                  <p className="text-xs text-[#666] dark:text-[#aaa] leading-relaxed mb-4">
                    We're excited to have you join us. AfroYap is a growing community built to connect people across African and Afro-diasporic identities, cultures, and conversations. We're continuously growing and improving — thank you for being part of the journey.
                  </p>

                  {/* The inline Community Rules summary was removed: it
                      restated a fraction of the rules next to the links that
                      hold all of them, and a partial copy of a legal document
                      is a copy that goes out of date. The Terms and Privacy
                      Policy links below are the canonical text. */}

                  {!betaAgreed && (
                    <div className="mb-2">
                      <p className="text-xs font-bold text-[#4478ea] dark:text-[#4478ea] animate-pulse">
                        Tap to Agree ↓
                      </p>
                    </div>
                  )}

                  <div className="flex items-center gap-3 w-full bg-black/5 dark:bg-[#2a2a2a] p-3 rounded-lg border border-black/5 dark:border-white/5" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      id="beta-agree"
                      checked={betaAgreed}
                      onCheckedChange={(checked) => setBetaAgreed(checked as boolean)}
                      className="w-5 h-5 rounded-md"
                      // Inline, not classes: the bundle has no
                      // data-[state=checked]:bg-green-500 or border-green-500,
                      // so those silently did nothing and the primitive's
                      // bg-primary won — which is why "agreed" looked white.
                      style={betaAgreed
                        ? { backgroundColor: SUCCESS, borderColor: SUCCESS, color: ON_SUCCESS }
                        : { borderColor: '#4478ea' }}
                    />
                    <label
                      htmlFor="beta-agree"
                      className="text-xs font-normal text-[#1a1a1a] dark:text-white cursor-pointer select-none text-left flex-1 leading-relaxed"
                      style={{ WebkitTextStroke: '0px' }}
                    >
                       I agree to the{' '}
                       <button
                         type="button"
                         onClick={(e) => { e.stopPropagation(); setLegalDoc('terms'); }}
                         className="underline font-medium text-[#4478ea]"
                         style={{ WebkitTextStroke: '0px' }}
                       >Terms of Service</button>
                       {' '}and{' '}
                       <button
                         type="button"
                         onClick={(e) => { e.stopPropagation(); setLegalDoc('privacy'); }}
                         className="underline font-medium text-[#4478ea]"
                         style={{ WebkitTextStroke: '0px' }}
                       >Privacy Policy</button>, and understand that objectionable content or abusive behavior is not tolerated.
                    </label>
                  </div>
                </div>
              </motion.div>
            )}

          </div>
        </motion.div>
      </div>

      {/* Fixed Footer with Navigation */}
      <div
        className="flex-none px-4 pt-4 pb-6 bg-[#eddeb7]/95 dark:bg-[#0D0D0D]/98 backdrop-blur-xl border-t border-black/5 dark:border-white/5 z-50 shadow-[0_-8px_30px_rgba(0,0,0,0.08)]"
        style={{ paddingBottom: 'max(24px, env(safe-area-inset-bottom))' }}
      >
        <div className="max-w-md mx-auto flex gap-3">
          {currentStep > 1 && (
            <Button
              onClick={() => setCurrentStep(currentStep - 1)}
              variant="outline"
              className="flex-1 h-11 text-xs font-bold border-[#4478ea]/30 text-[#4478ea] hover:bg-[#4478ea]/10 rounded-xl uppercase tracking-wider transition-all"
            >
              Back
            </Button>
          )}
            <Button
              onClick={handleNext}
              disabled={!canProceedToNext() || completing}
              className={`flex-1 h-11 text-xs font-bold rounded-xl uppercase tracking-wider shadow-lg transition-all border-none ${
                (!canProceedToNext() || completing)
                  ? "bg-[#CCCCCC] dark:bg-[#2A2A2A] text-[#666666] dark:text-[#505050] cursor-not-allowed"
                  : "bg-linear-to-r from-[#4478ea] to-[#2d1ef1] text-white hover:opacity-90"
              }`}
            >
            {currentStep === totalSteps ? (
              <>
                {completing ? 'Completing...' : 'Finish Profile'} <Check className="w-4 h-4 ml-2" />
              </>
            ) : (
              <>
                Next Step <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>

      {legalDoc && <LegalModal type={legalDoc} onClose={() => setLegalDoc(null)} />}

      <IdentityExpansionModal
        isOpen={showIdentityModal}
        onClose={() => setShowIdentityModal(false)}
        category={activeCategory}
        onSave={handleIdentitiesSave}
        initialSelected={selectedIdentities}
      />

      {/* Media Trimmer Modal */}
      <MediaTrimmerModal
        isOpen={mediaModalOpen}
        onClose={() => {
          setMediaModalOpen(false);
          setMediaUrl(null);
          setOriginalMediaBlob(null);
          setTempMatchPhoto(null);
          setTempVideoBlob(null);
          setTempTrimStart(undefined);
          setTempTrimEnd(undefined);
          setMediaStep('match');
        }}
        mediaUrl={mediaUrl || ''}
        mediaBlob={originalMediaBlob || undefined}
        mediaType={mediaType}
        onSave={handleMediaSave}
        aspectRatio={mediaStep === 'match' ? 3 / 4 : 1}
        title={mediaStep === 'match' ? "Profile Pane Preview" : "Avatar Display"}
        circular={false}
      />
    </div >
  );
}
